/**
 * Tasks: eslatmalar Overdue / Today / Upcoming bo'limlarida (eskiz 6).
 * Vaqt ikki zonada: foydalanuvchiniki va driverniki (AQSh Central yoki Toshkent).
 */
import { usePageSize } from "@/lib/pageSize";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, Plus, Search, X } from "lucide-react";
import { api, ApiError, getAllPages, type Paginated } from "@/lib/api";
import { cn, dayjs, formatDate } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import type { Me } from "@/features/auth/types";
import { driverKeys, useReminderAction, useUserOptions } from "@/features/drivers/api";
import type { DriverListItem, Reminder, ReminderKind } from "@/features/drivers/types";
import { StatusPill } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Field, FilterSelect, Input, Select, Textarea } from "@/components/ui/field";
import { EmptyState, ErrorState, PageHeader, PageLoading } from "@/components/ui/misc";
import { Modal } from "@/components/ui/modal";
import { FilterChips, Pagination } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { ContextMenu, type MenuEntry } from "@/components/ui/context-menu";
import { callPhone, copyText, openInNewTab } from "@/lib/clipboard";
import { driverTz } from "@/features/drivers/timezones";
import { snoozeOptions, taskMenu } from "@/features/tasks/menu";
import { AddReminderModal as DriverReminderModal } from "@/pages/drivers/AddReminderModal";
import { QuickActivityModal } from "@/pages/drivers/QuickActivityModal";

type Chip = "all" | "call_back" | "send_docs" | "follow_up" | "expiry" | "retention_check";
// label: tarjima kaliti (tasks:chips.* yoki common:reminder.*)
const CHIPS: { key: Chip; label: string; kinds: ReminderKind[] }[] = [
  { key: "all", label: "tasks:chips.all", kinds: [] },
  { key: "call_back", label: "common:reminder.call_back", kinds: ["call_back"] },
  { key: "send_docs", label: "common:reminder.send_docs", kinds: ["send_docs"] },
  { key: "follow_up", label: "common:reminder.follow_up", kinds: ["follow_up"] },
  { key: "expiry", label: "tasks:chips.expiry", kinds: ["cdl_expiry", "medical_expiry"] },
  { key: "retention_check", label: "common:reminder.retention_check", kinds: ["retention_check"] },
];
const TASHKENT = "Asia/Tashkent";
const CENTRAL = "America/Chicago";

interface Summary {
  overdue: number;
  today: number;
  upcoming: number;
}

export function TasksPage() {
  const me = useMe();
  if (!me.data) return <PageLoading />;
  return <Tasks me={me.data} />;
}

function Tasks({ me }: { me: Me }) {
  const { t } = useTranslation("tasks");
  const qc = useQueryClient();
  const [chip, setChip] = useState<Chip>("all");
  const [params, setParams] = useSearchParams();
  // Xodim profilidagi "Kechikkan vazifalar" kartasidan: faqat shu xodimga biriktirilgan vazifalar (backend assigned_to)
  const assignee = params.get("assignee") ?? "";
  const [adding, setAdding] = useState(params.get("new") === "1"); // ⌘K dan "Yangi eslatma"
  const [showDone, setShowDone] = useState(false);
  const tz = me.timezone || TASHKENT;
  // Ikkinchi zona: foydalanuvchi Toshkentda bo'lsa driver vaqti (Central), aks holda Toshkent
  const otherTz = tz === TASHKENT ? CENTRAL : TASHKENT;

  const byAssignee = assignee ? { assigned_to: assignee } : {};
  // Sahifalash va chip filtri server'da (2026-10-25: avval dastlabki 100 tasi olinib, qolgani "bir nechtasini bajaring" deb yashirinardi)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize("tasks", 100);
  const chipKinds = CHIPS.find((c) => c.key === chip)?.kinds ?? [];
  const summary = useQuery({
    queryKey: ["reminder", "summary", assignee],
    queryFn: () => api.get<Summary>("/api/reminders/summary/", { query: byAssignee }),
  });
  const pending = useQuery({
    queryKey: ["reminder", "list", "pending", assignee, chip, page, pageSize],
    queryFn: () =>
      api.get<Paginated<Reminder>>("/api/reminders/", {
        query: { done: false, page, page_size: pageSize, ordering: "due_at", kind: chipKinds.length ? chipKinds : undefined, ...byAssignee },
      }),
    placeholderData: keepPreviousData,
  });
  // Bugun bajarilganlar -- server'da (kun boshi va tur bo'yicha), hammasi: avval oxirgi 50 tadan brauzerda ajratilardi
  const doneSince = dayjs().tz(tz).startOf("day").toISOString();
  const done = useQuery({
    queryKey: ["reminder", "list", "done", assignee, doneSince, chipKinds],
    queryFn: () => getAllPages<Reminder>("/api/reminders/", { done: true, done_after: doneSince, ordering: "-done_at", kind: chipKinds.length ? chipKinds : undefined, ...byAssignee }),
    placeholderData: keepPreviousData,
  });
  const setAssignee = (id: string) => {
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (id) next.set("assignee", id);
      else next.delete("assignee");
      return next;
    });
    setPage(1);
  };
  // Owner va menejer kimning vazifalarini ko'rishni o'zi tanlaydi (2026-10-28: avval faqat profil'dan kelgan chip edi)
  const manages = me.role === "owner" || me.role === "branch_manager";
  // Ism va tanlov: xodimlar ro'yxatidan (doira bo'yicha), bloklangan bo'lsa -- vazifaning o'zidan
  const staff = useUserOptions({}, manages || !!assignee);
  const assigneeName = assignee
    ? (staff.data?.results.find((u) => String(u.id) === assignee)?.full_name ??
      pending.data?.results.find((r) => String(r.assigned_to?.id) === assignee)?.assigned_to?.display_name ??
      "")
    : "";
  const action = useReminderAction();
  const navigate = useNavigate();
  const { t: tm } = useTranslation("menu");
  // O'ng tugma menyusidan ochiladigan oynalar (driver faolligi, kuzatuv eslatmasi)
  const [dialog, setDialog] = useState<{ kind: "activity"; r: Reminder; type: "call" | "note" } | { kind: "reminder"; r: Reminder } | null>(null);

  const menuFor = (r: Reminder): MenuEntry[] =>
    taskMenu(
      r,
      {
        openDriver: () => navigate(`/drivers/${r.driver}`),
        openDriverNewTab: () => openInNewTab(`/drivers/${r.driver}`),
        call: callPhone,
        copy: (text) => void copyText(text),
        done: () => run(r.id, "done", undefined, t("common:actions.done")),
        undo: () => run(r.id, "undo", undefined, t("toast.reopened")),
        snooze: (o) => run(r.id, "snooze", o.at.toISOString(), t("toast.snoozed", { when: o.label })),
        logActivity: (type) => setDialog({ kind: "activity", r, type }),
        addReminder: () => setDialog({ kind: "reminder", r }),
      },
      tm,
      snoozeOptions(tz, t),
    );

  const now = dayjs();
  const dayEnd = now.tz(tz).endOf("day");

  const groups = useMemo(() => {
    const rows = pending.data?.results ?? [];
    const overdue: Reminder[] = [];
    const today: Reminder[] = [];
    const upcoming: Reminder[] = [];
    for (const r of rows) {
      const due = dayjs(r.effective_due);
      if (due.isBefore(now)) overdue.push(r);
      else if (due.isBefore(dayEnd)) today.push(r);
      else upcoming.push(r);
    }
    const byDue = (a: Reminder, b: Reminder) => dayjs(a.effective_due).valueOf() - dayjs(b.effective_due).valueOf();
    return { overdue: overdue.sort(byDue), today: today.sort(byDue), upcoming: upcoming.sort(byDue) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending.data, chip]);

  const completedToday = done.data?.results ?? [];

  function run(id: number, kind: "done" | "undo" | "snooze", snooze_until?: string, label?: string) {
    action
      .mutateAsync({ id, action: kind, snooze_until })
      .then(() => {
        qc.invalidateQueries({ queryKey: ["reminder"] });
        if (label) toast.success(label);
      })
      .catch((e) => toast.error(e instanceof ApiError ? e.message : t("toast.updateFailed")));
  }

  const s = summary.data;
  const counts = s ? { overdue: s.overdue, today: s.today, upcoming: s.upcoming } : null;
  // Boshqa xodimning vazifalari ko'rilayotganda "Mening eslatmalarim" emas, uning ismi
  const subtitle = counts ? (assignee && assigneeName ? t("subtitleFor", { name: assigneeName, ...counts }) : t("subtitle", counts)) : t("myReminders");
  const total = (pending.data?.results ?? []).length;

  return (
    <div>
      <PageHeader
        title={t("title")}
        subtitle={subtitle}
        actions={
          <Button variant="primary" onClick={() => setAdding(true)}>
            <Plus size={14} /> {t("addReminder")}
          </Button>
        }
      >
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <FilterChips<Chip>
              items={CHIPS.map((c) => ({ key: c.key, label: t(c.label) }))}
              value={chip}
              onChange={(k) => {
                setChip(k);
                setPage(1);
              }}
            />
            {manages ? (
              <FilterSelect value={assignee} onChange={(e) => setAssignee(e.target.value)} className="w-48" aria-label={t("assigneeLabel")}>
                <option value="">{t("assigneeAll")}</option>
                {(staff.data?.results ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                  </option>
                ))}
                {assignee && !staff.data?.results.some((u) => String(u.id) === assignee) && <option value={assignee}>{assigneeName || t("assigneeChipUnknown")}</option>}
              </FilterSelect>
            ) : assignee && (
              // Recruiter: URL bilan kelgan filtr (o'z profilidan) -- tanlangan filtr kabi sariq, × bilan olib tashlanadi
              <button
                type="button"
                onClick={() => setAssignee("")}
                className="inline-flex h-9 items-center gap-1.5 rounded-control border border-accent bg-accent-soft px-3 text-[13px] text-ink"
                title={t("common:actions.clearFilters")}
              >
                {assigneeName ? t("assigneeChip", { name: assigneeName }) : t("assigneeChipUnknown")}
                <X size={14} strokeWidth={1.75} aria-hidden />
              </button>
            )}
          </div>
          <span className="text-[12px] text-muted">{t("sort")}</span>
        </div>
      </PageHeader>

      {pending.isError ? (
        <ErrorState message={pending.error instanceof ApiError ? pending.error.message : t("loadFailed")} onRetry={() => pending.refetch()} />
      ) : pending.isPending ? (
        <PageLoading />
      ) : total === 0 && completedToday.length === 0 ? (
        <EmptyState title={t("empty.title")} description={t("empty.desc")} />
      ) : (
        <div className="space-y-5">
          <Section title={t("common:time.overdue")} count={groups.overdue.length} tone="danger">
            {groups.overdue.map((r) => (
              <Row key={r.id} r={r} tz={tz} otherTz={otherTz} mode="overdue" onDone={() => run(r.id, "done", undefined, t("common:actions.done"))} onSnooze={(iso, label) => run(r.id, "snooze", iso, t("toast.snoozed", { when: label }))} busy={action.isPending} menu={menuFor} />
            ))}
          </Section>
          <Section title={t("common:time.today")} count={groups.today.length}>
            {groups.today.map((r) => (
              <Row key={r.id} r={r} tz={tz} otherTz={otherTz} mode="today" onDone={() => run(r.id, "done", undefined, t("common:actions.done"))} onSnooze={(iso, label) => run(r.id, "snooze", iso, t("toast.snoozed", { when: label }))} busy={action.isPending} menu={menuFor} />
            ))}
          </Section>
          <Section title={t("upcoming")} count={groups.upcoming.length}>
            {groups.upcoming.map((r) => (
              <Row key={r.id} r={r} tz={tz} otherTz={otherTz} mode="upcoming" onDone={() => run(r.id, "done", undefined, t("common:actions.done"))} onSnooze={(iso, label) => run(r.id, "snooze", iso, t("toast.snoozed", { when: label }))} busy={action.isPending} menu={menuFor} />
            ))}
          </Section>
          {pending.data && (
            <Pagination
              page={pending.data.page}
              pages={pending.data.pages}
              count={pending.data.count}
              pageSize={pending.data.page_size}
              onPage={setPage}
              onPageSize={(s) => {
                setPageSize(s);
                setPage(1);
              }}
              className="px-0"
            />
          )}

          {completedToday.length > 0 && (
            <Card>
              <button type="button" onClick={() => setShowDone((v) => !v)} className="flex w-full items-center justify-between px-5 py-3 text-left text-[13px] font-semibold">
                <span>
                  {t("completedToday")} <span className="ml-1 font-normal text-muted">· {completedToday.length}</span>
                </span>
                <ChevronDown size={16} className={cn("text-muted transition-transform", showDone && "rotate-180")} />
              </button>
              {showDone && (
                <div className="divide-y divide-line border-t border-line">
                  {completedToday.map((r) => (
                    <ContextMenu key={r.id} items={() => menuFor(r)}>
                      <div className="flex items-center gap-3 px-5 py-2 text-[13px] text-muted data-[state=open]:bg-primary-soft/50">
                      <span className="line-through">
                        {t(`common:reminder.${r.kind}`)} · {r.driver_name}
                        {r.note ? ` · ${r.note}` : ""}
                      </span>
                      <span className="ml-auto text-[11px]">{dayjs(r.done_at).tz(tz).format("HH:mm")}</span>
                      <Button size="sm" variant="ghost" onClick={() => run(r.id, "undo", undefined, t("toast.reopened"))} disabled={action.isPending}>
                        {t("common:actions.undo")}
                      </Button>
                      </div>
                    </ContextMenu>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      <AddReminderModal open={adding} onClose={() => setAdding(false)} tz={tz} />
      {dialog?.kind === "activity" && (
        <QuickActivityModal driver={{ id: dialog.r.driver, full_name: dialog.r.driver_name }} type={dialog.type} open onOpenChange={(o) => !o && setDialog(null)} />
      )}
      {dialog?.kind === "reminder" && (
        <DriverReminderModal driverId={dialog.r.driver} dTz={driverTz(dialog.r.driver_state)} open onOpenChange={(o) => !o && setDialog(null)} defaultAssignee={dialog.r.assigned_to?.id ?? null} />
      )}
    </div>
  );
}

function Section({ title, count, tone, children }: { title: string; count: number; tone?: "danger"; children: React.ReactNode }) {
  if (count === 0) return null;
  return (
    <div>
      <div className={cn("mb-2 text-[13px] font-semibold", tone === "danger" ? "text-danger" : "text-ink")}>
        {title} <span className="font-normal text-muted">· {count}</span>
      </div>
      <div className="divide-y divide-line rounded-card border border-border/60 bg-surface shadow-card">{children}</div>
    </div>
  );
}

function Row({ r, tz, otherTz, mode, onDone, onSnooze, busy, menu }: { r: Reminder; tz: string; otherTz: string; mode: "overdue" | "today" | "upcoming"; onDone: () => void; onSnooze: (iso: string, label: string) => void; busy: boolean; menu: (r: Reminder) => MenuEntry[] }) {
  const { t } = useTranslation("tasks");
  const due = dayjs(r.effective_due);
  return (
    <ContextMenu items={() => menu(r)}>
      <div className="flex items-center gap-3 px-4 py-2.5 text-[13px] data-[state=open]:bg-primary-soft/50">
      <button type="button" aria-label={t("common:actions.done")} onClick={onDone} disabled={busy} className={cn("grid h-6 w-6 shrink-0 place-items-center rounded-full border-[1.5px] transition-colors hover:border-ink disabled:opacity-50", mode === "overdue" ? "border-warning" : "border-faint")} />
      <div className="w-[120px] shrink-0 leading-tight">
        <WhenLabel due={due} tz={tz} otherTz={otherTz} mode={mode} />
      </div>
      <span className={cn("inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold", mode === "overdue" ? "bg-danger-soft text-danger" : "bg-fill text-ink")}>
        {t(`common:reminder.${r.kind}`)}
      </span>
      <div className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <Link to={`/drivers/${r.driver}`} className="truncate font-semibold hover:underline">
            {r.driver_name}
          </Link>
          <StatusPill status={r.driver_status} className="text-[10px]" />
        </span>
        {r.note && <span className="block truncate text-[12px] text-ink-soft">{r.note}</span>}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button size="sm" onClick={onDone} disabled={busy}>
          {t("common:actions.done")}
        </Button>
        <SnoozeMenu tz={tz} onPick={onSnooze} disabled={busy} />
      </div>
      </div>
    </ContextMenu>
  );
}

function WhenLabel({ due, tz, otherTz, mode }: { due: dayjs.Dayjs; tz: string; otherTz: string; mode: "overdue" | "today" | "upcoming" }) {
  const { t } = useTranslation("tasks");
  const local = due.tz(tz);
  if (mode === "overdue") {
    const hours = dayjs().diff(due, "hour");
    const late = hours >= 24 ? t("late.days", { count: Math.floor(hours / 24) }) : hours >= 1 ? t("late.hours", { count: hours }) : t("late.justNow");
    return (
      <>
        <span className="block font-semibold text-danger">{local.format("MMM D")}</span>
        <span className="block text-[11px] text-danger">{late}</span>
      </>
    );
  }
  if (mode === "today") {
    return (
      <>
        <span className="block font-semibold">
          {local.format("HH:mm")} <span className="font-normal text-muted">{zoneLabel(tz, t)}</span>
        </span>
        <span className="block text-[11px] text-muted">
          {due.tz(otherTz).format("HH:mm")} {zoneLabel(otherTz, t)}
        </span>
      </>
    );
  }
  return (
    <>
      <span className="block font-semibold">{local.format("MMM D")}</span>
      <span className="block text-[11px] text-muted">{local.format("HH:mm")}</span>
    </>
  );
}

function zoneLabel(tz: string, t: TFunction<"tasks">): string {
  if (tz === TASHKENT) return t("zone.tashkent");
  if (tz === CENTRAL) return "CST";
  if (tz === "America/New_York") return "EST";
  if (tz === "America/Denver") return "MST";
  if (tz === "America/Los_Angeles") return "PST";
  return tz.split("/").pop()?.replace("_", " ") ?? tz;
}

function SnoozeMenu({ tz, onPick, disabled }: { tz: string; onPick: (iso: string, label: string) => void; disabled: boolean }) {
  const { t } = useTranslation("tasks");
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  // Menyu bilan bir xil variantlar (features/tasks/menu.ts)
  const options = snoozeOptions(tz, t);
  function pick(at: dayjs.Dayjs, label: string) {
    setOpen(false);
    onPick(at.toISOString(), label);
  }
  // Popover (portal): ekran chetiga yetsa o'zi suriladi (2026-10-25: o'ng chetdagi qatorda "O'rnatish" ekrandan chiqib ketardi)
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button size="sm" variant="ghost" disabled={disabled} aria-haspopup="menu" aria-expanded={open}>
          {t("snooze.button")} <ChevronDown size={12} />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        {/* Sichqoncha bilan ochilganda birinchi variant fokus halqasi bilan "tanlangandek" ko'rinmasin (InfoTip kabi) */}
        <Popover.Content role="menu" align="end" sideOffset={4} collisionPadding={12} onOpenAutoFocus={(e) => e.preventDefault()} className="fade-in z-50 w-64 rounded-[10px] border border-border bg-surface p-1 shadow-menu">
          {options.map((o) => (
            <button key={o.label} type="button" role="menuitem" onClick={() => pick(o.at, o.label)} className="block w-full rounded px-3 py-1.5 text-left text-[13px] hover:bg-fill/60">
              {o.label}
            </button>
          ))}
          {/* Maydon va tugma ustma-ust: datetime maydoni keng, yonma-yon menyuga sig'maydi */}
          <div className="mt-1 space-y-1.5 border-t border-line px-2 pt-2 pb-1">
            <label className="block text-[11px] text-muted">{t("snooze.custom", { zone: zoneLabel(tz, t) })}</label>
            <Input type="datetime-local" value={custom} onChange={(e) => setCustom(e.target.value)} className="h-8 w-full text-[12px]" />
            <Button
              size="sm"
              variant="primary"
              className="w-full justify-center"
              disabled={!custom}
              onClick={() => {
                const at = dayjs.tz(custom, tz);
                pick(at, at.format("MMM D, HH:mm"));
              }}
            >
              {t("snooze.set")}
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function AddReminderModal({ open, onClose, tz }: { open: boolean; onClose: () => void; tz: string }) {
  const { t } = useTranslation("tasks");
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [driver, setDriver] = useState<DriverListItem | null>(null);
  const [kind, setKind] = useState<ReminderKind>("call_back");
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  // Qidiruv 300 ms kechikish bilan
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const results = useQuery({
    queryKey: ["driver", "search", debounced],
    queryFn: () => api.get<Paginated<DriverListItem>>("/api/drivers/", { query: { q: debounced, page_size: 8, ordering: "last_name" } }),
    enabled: open && debounced.length >= 2 && driver === null,
  });

  // useCreateReminder(id) hook'i driver tanlangunga qadar chaqirilmaydi, shuning uchun lokal mutation
  const create = useMutation({
    mutationFn: (data: { driverId: number; kind: ReminderKind; due_at: string; note: string }) =>
      api.post<Reminder>(`/api/drivers/${data.driverId}/reminders/`, { kind: data.kind, due_at: data.due_at, note: data.note }),
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ["reminder"] });
      qc.invalidateQueries({ queryKey: driverKeys.reminders(vars.driverId) });
      qc.invalidateQueries({ queryKey: driverKeys.detail(vars.driverId) });
    },
  });

  function reset() {
    setSearch("");
    setDebounced("");
    setDriver(null);
    setKind("call_back");
    setDue("");
    setNote("");
    setErrors({});
  }

  async function submit() {
    const errs: Record<string, string[]> = {};
    if (!driver) errs.driver = [t("add.chooseDriver")];
    if (!due) errs.due_at = [t("add.chooseDateTime")];
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    try {
      await create.mutateAsync({ driverId: driver!.id, kind, due_at: dayjs.tz(due, tz).toISOString(), note });
      toast.success(t("toast.added"), `${t(`common:reminder.${kind}`)} · ${driver!.full_name}`);
      reset();
      onClose();
    } catch (e) {
      if (e instanceof ApiError) setErrors(e.fields ?? { due_at: [e.message] });
      else toast.error(t("toast.addFailed"));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
      title={t("add.title")}
      description={t("add.desc")}
      footer={
        <>
          <Button onClick={onClose} disabled={create.isPending}>
            {t("common:actions.cancel")}
          </Button>
          <Button variant="primary" loading={create.isPending} onClick={submit}>
            {t("addReminder")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t("add.driver")} required error={errors.driver}>
          {driver ? (
            <div className="flex items-center justify-between rounded-[10px] border border-border px-3 py-2 text-[13px]">
              <span>
                <span className="font-semibold">{driver.full_name}</span> <span className="text-muted">· {driver.phone_display}</span> <StatusPill status={driver.status} className="ml-1 text-[10px]" />
              </span>
              <button type="button" className="text-[12px] text-primary hover:underline" onClick={() => setDriver(null)}>
                {t("add.change")}
              </button>
            </div>
          ) : (
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted" />
              <Input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("add.searchPlaceholder")} className="pl-8" aria-invalid={!!errors.driver} />
              {debounced.length >= 2 && (
                <div className="mt-1 max-h-56 overflow-y-auto rounded-[10px] border border-border bg-surface">
                  {results.isPending ? (
                    <div className="px-3 py-2 text-[12px] text-muted">{t("add.searching")}</div>
                  ) : (results.data?.results ?? []).length === 0 ? (
                    <div className="px-3 py-2 text-[12px] text-muted">{t("add.noDrivers")}</div>
                  ) : (
                    results.data!.results.map((d) => (
                      <button key={d.id} type="button" onClick={() => setDriver(d)} className="flex w-full items-center justify-between px-3 py-2 text-left text-[13px] hover:bg-fill/60">
                        <span>
                          <span className="font-semibold">{d.full_name}</span> <span className="text-muted">· {d.phone_display}</span>
                        </span>
                        <StatusPill status={d.status} className="text-[10px]" />
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("add.type")} htmlFor="rem-kind">
            <Select id="rem-kind" value={kind} onChange={(e) => setKind(e.target.value as ReminderKind)}>
              {(["call_back", "send_docs", "follow_up", "retention_check"] as ReminderKind[]).map((k) => (
                <option key={k} value={k}>
                  {t(`common:reminder.${k}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("add.due", { zone: zoneLabel(tz, t) })} required htmlFor="rem-due" error={errors.due_at}>
            <Input id="rem-due" type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} aria-invalid={!!errors.due_at} />
          </Field>
        </div>
        <Field label={t("add.note")} htmlFor="rem-note" error={errors.note}>
          <Textarea id="rem-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("add.notePlaceholder")} className="min-h-[60px]" />
        </Field>
        {due && <p className="text-[11px] text-muted">{t("add.driverTime")} {dayjs.tz(due, tz).tz(tz === TASHKENT ? CENTRAL : TASHKENT).format("MMM D, HH:mm")} {zoneLabel(tz === TASHKENT ? CENTRAL : TASHKENT, t)} · {formatDate(dayjs.tz(due, tz).toISOString())}</p>}
      </div>
    </Modal>
  );
}
